/* eslint-disable react/no-unstable-nested-components */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */

import React, { RefObject, useEffect } from 'react';
import classNames from 'classnames';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  PaginationState,
  useReactTable,
} from '@tanstack/react-table';
import { SingleValue } from 'react-select';
import { localeDate } from 'src/localization/locale';
import { useTranslation } from 'react-i18next';
import styles from '../../tables/ContactTable/ContactTable.module.scss';
import Select, { Option } from '../../ui/Select/Select';
import TablePagination from '../../ui/TablePagination/TablePagination';
import Checkbox from '../../ui/Checkbox/Checkbox';
import { getPaginationOptions } from '../../../utils/general';

export interface ReturnsTableProps {
  containerRef: RefObject<HTMLDivElement>;
  data: any;
  onSelectionChange?: (selectedOrders: string[]) => void;
  readonly?: boolean;
}

const columnHelper = createColumnHelper<any>();
interface ColumnHelperProps {
  t: (key: string) => string;
}

const OrdersSearchTable: React.FC<ReturnsTableProps> = ({
  containerRef,
  data,
  onSelectionChange,
  readonly = false,
}) => {
  const { t } = useTranslation();
  const getColumns = ({ t }: ColumnHelperProps) => [
    columnHelper.accessor(row => row.name, {
      id: 'checkbox',
      size: 200,
      meta: {
        label: t('tracking.enter_order_number'),
      },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => (
        <div className="flex items-center gap-[12px]">
          <Checkbox
            checked={readonly ? true : info.row.getIsSelected()}
            disabled={readonly}
            indeterminate={info.row.getIsSomeSelected()}
            onChange={info.row.getToggleSelectedHandler()}
          />
          <span className="typo-text-14--medium">{info.getValue()}</span>
        </div>
      ),
    }),
    columnHelper.accessor(row => row.partner.name, {
      id: 'contact',
      size: 200,
      meta: {
        label: t('contact.title'),
      },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => (
        <div className="flex flex-col gap-y-4">
          <span className="typo-text-14"> {info.getValue()} </span>
        </div>
      ),
    }),
    columnHelper.accessor(row => row.create_date, {
      id: 'create_date',
      size: 200,
      meta: {
        label: t('general.order_date'),
      },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => {
        const date = new Date(info.getValue());
        const formattedDate = localeDate(date);

        return (
          <div className="flex flex-col gap-y-8">
            <span className="typo-text-14 text-full">{formattedDate}</span>
          </div>
        );
      },
    }),
  ];

  const rootRef = React.useRef<HTMLDivElement>(null);
  const [rowSelection, setRowSelection] = React.useState({});
  const options = getPaginationOptions();

  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: parseInt(options[1].value, 10),
  });

  const table = useReactTable({
    data: readonly ? data : data?.items || [],
    columns: getColumns({ t }),
    state: {
      rowSelection,
      pagination,
    },
    enableRowSelection: true,
    enableMultiRowSelection: false,
    onRowSelectionChange: setRowSelection,
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    getCoreRowModel: getCoreRowModel(),
  });

  const handlePerPageUpdate = (selectedOption: SingleValue<Option>) => {
    if (selectedOption) {
      setPagination({ pageIndex: 0, pageSize: Number(selectedOption.value) });
      if (containerRef) {
        containerRef.current?.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const handleTablePaginationClick = (direction: 'previous' | 'next') => {
    const newIndex = direction === 'next' ? pagination.pageIndex + 1 : pagination.pageIndex - 1;
    setPagination({ ...pagination, pageIndex: newIndex });
    if (containerRef) {
      containerRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    const selectedOrders = Object.keys(rowSelection);
    if (onSelectionChange) {
      onSelectionChange(selectedOrders);
    }
  }, [rowSelection, data]);

  return (
    <div className={styles.tableContent}>
      <div
        ref={rootRef}
        className={classNames(
          styles.root,
          'max-w-[300px] xs:max-w-[340px] sm:max-w-[400px] md:max-w-none overflow-x-auto',
        )}>
        <div
          className={classNames(
            styles.tableWrapper,
            `${table.getRowModel().rows.length > 1 ? 'min-h-[40rem]' : 'min-h-[10rem]'}`,
            `${readonly && 'surface-elevated-more'}`,
            'w-full',
          )}>
          <table>
            <thead>
              {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <th
                      {...{
                        key: header.id,
                        colSpan: header.colSpan,
                      }}>
                      <div className={styles.headerColumn} onClick={header.column.getToggleSortingHandler()}>
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.length > 0 ? (
                table.getRowModel().rows.map(row => (
                  <tr
                    key={row.id}
                    onClick={() => {
                      if (!readonly) {
                        row.toggleSelected();
                      }
                    }}
                    className={classNames('cursor-pointer', row.getIsSelected() && 'bg-surface-hover')}>
                    {row.getVisibleCells().map(cell => (
                      <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={table.getAllColumns().length}
                    className="text-center w-full py-4 typo-text-14--bold text-faded">
                    <div className="flex justify-center items-center h-20">{t('general.no_search_results')}</div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {table.getRowModel().rows.length > 0 && !readonly && (
          <div className="w-full my-20 flex items-center">
            <div className="flex gap-x-10 flex-1 items-center">
              <div>
                <Select onChange={handlePerPageUpdate} options={options} defaultValue={options[0]} />
              </div>
              <div className="typo-text-12 text-faded">{t('general.per_page')}</div>
            </div>
            <div className="ml-auto">
              <TablePagination
                maxPage={data?.pages || 0}
                onPaginationClick={handleTablePaginationClick}
                currentPage={pagination.pageIndex}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrdersSearchTable;
